import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import type { SpecificScatterPoint } from "@/types";

interface GroupSizeDistributionChartProps {
  data: SpecificScatterPoint[];
}

const BUCKETS = [
  { label: "1-100", min: 1, max: 100 },
  { label: "101-250", min: 101, max: 250 },
  { label: "251-500", min: 251, max: 500 },
  { label: "501-1,000", min: 501, max: 1000 },
  { label: "1,001+", min: 1001, max: Infinity },
];

/**
 * Bar chart showing the distribution of policies by group size (enrollment).
 */
export const GroupSizeDistributionChart = ({ data }: GroupSizeDistributionChartProps) => {
  const { theme } = useAppTheme();

  const chartData = useMemo(() => {
    const counts = BUCKETS.map((b) => ({ name: b.label, count: 0 }));
    for (const d of data) {
      if (d.group_size <= 0) continue;
      const idx = BUCKETS.findIndex((b) => d.group_size >= b.min && d.group_size <= b.max);
      if (idx >= 0) counts[idx].count += 1;
    }
    return counts;
  }, [data]);

  const hasData = chartData.some((d) => d.count > 0);
  if (!hasData) return <EmptyChartState />;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} />
        <Tooltip contentStyle={getChartTooltipStyle(theme)} />
        <Bar dataKey="count" name="Policies" fill={theme.palette.primary.main} />
      </BarChart>
    </ResponsiveContainer>
  );
};
